import { createTheme } from '@mui/material/styles';
import { breakpointValues } from './breakpoints.js';
import { hpColors, hpFonts, hpShadow, hpDuration } from './happypawsTokens.js';

/**
 * Thème MUI admin Happy Paws — outil de travail sobre.
 * Couleurs HP, typo UI uniquement (pas de serif), densité confortable.
 *
 * @param {{ primary?: string, secondary?: string, displayName?: string }} themeInput
 * @param {{ mode?: 'light' | 'dark' }} [opts]
 */
export default function buildAdminTheme(themeInput = {}, opts = {}) {
  const mode = opts.mode === 'dark' ? 'dark' : 'light';
  const p = themeInput?.primary || hpColors.primary;
  const s = themeInput?.secondary || hpColors.secondary;
  const isDark = mode === 'dark';

  const pal = isDark
    ? {
        text: {
          primary: hpColors.darkTextPrimary,
          secondary: '#C4B5AB',
          disabled: 'rgba(250, 241, 237, 0.38)',
        },
        background: {
          default: '#1C1815',
          paper: '#2A211C',
        },
        divider: 'rgba(250, 241, 237, 0.12)',
        action: {
          hover: 'rgba(250, 241, 237, 0.06)',
          selected: 'rgba(212, 154, 116, 0.16)',
        },
      }
    : {
        text: {
          primary: hpColors.textPrimary,
          secondary: hpColors.textSecondary,
          disabled: 'rgba(85, 61, 45, 0.38)',
        },
        background: {
          default: '#F5F0EC',
          paper: '#FFFCFA',
        },
        divider: hpColors.border,
        action: {
          hover: 'rgba(85, 61, 45, 0.04)',
          selected: 'rgba(193, 131, 94, 0.12)',
        },
      };

  const fontUi = hpFonts.ui;

  return createTheme({
    breakpoints: { values: breakpointValues },
    palette: {
      mode,
      primary: {
        main: isDark ? hpColors.darkPrimary : p,
        contrastText: isDark ? hpColors.textInk : hpColors.onPrimary,
      },
      secondary: {
        main: s,
        contrastText: hpColors.textInk,
      },
      background: pal.background,
      divider: pal.divider,
      text: pal.text,
      action: {
        hover: pal.action.hover,
        selected: pal.action.selected,
      },
      error: { main: '#d32f2f' },
      warning: { main: '#ed6c02' },
      success: { main: '#2e7d32' },
      info: { main: '#0288d1' },
    },
    typography: {
      fontFamily: fontUi,
      fontSize: 16,
      h1: {
        fontFamily: fontUi,
        fontSize: '2rem',
        fontWeight: 700,
        lineHeight: 1.22,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontFamily: fontUi,
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: 1.25,
        letterSpacing: '-0.015em',
      },
      h3: {
        fontFamily: fontUi,
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontFamily: fontUi,
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h5: {
        fontFamily: fontUi,
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.35,
      },
      h6: {
        fontFamily: fontUi,
        fontSize: '0.9375rem',
        fontWeight: 600,
        lineHeight: 1.35,
      },
      body1: {
        fontFamily: fontUi,
        fontSize: '1rem',
        lineHeight: 1.5,
        fontWeight: 400,
      },
      body2: {
        fontFamily: fontUi,
        fontSize: '0.875rem',
        lineHeight: 1.5,
        fontWeight: 400,
        color: pal.text.secondary,
      },
      subtitle2: {
        fontFamily: fontUi,
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: pal.text.secondary,
      },
      caption: {
        fontFamily: fontUi,
        fontSize: '0.75rem',
        lineHeight: 1.45,
        fontWeight: 400,
        color: isDark ? 'rgba(250, 241, 237, 0.55)' : hpColors.textTertiary,
      },
      button: {
        fontFamily: fontUi,
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: 'normal',
      },
    },
    shape: { borderRadius: 8 },
    transitions: {
      duration: {
        shortest: hpDuration.fast,
        standard: hpDuration.base,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: fontUi,
            fontSize: '1rem',
            lineHeight: 1.5,
            backgroundColor: pal.background.default,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'background-color 0.15s ease-out, box-shadow 0.15s ease-out, color 0.15s ease-out',
            '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed' },
            minHeight: 40,
            paddingLeft: 16,
            paddingRight: 16,
          },
          sizeSmall: { minHeight: 36, paddingLeft: 12, paddingRight: 12 },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: hpShadow.surface },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed' },
          },
          sizeMedium: { padding: 8 },
          sizeSmall: { padding: 6 },
        },
      },
      MuiButtonBase: {
        defaultProps: { disableRipple: false },
        styleOverrides: {
          root: {
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: p,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 12,
          },
          elevation1: { boxShadow: hpShadow.surface },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: hpShadow.surface,
            border: '1px solid',
            borderColor: pal.divider,
            borderRadius: 12,
          },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: '0.9375rem',
            backgroundColor: pal.background.paper,
            transition: 'border-color 0.15s ease-out, box-shadow 0.15s ease-out',
          },
          input: {
            fontSize: '0.9375rem',
            // évite zoom iOS sur tablette admin
            '@media (max-width: 959px)': { fontSize: '16px' },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: '0.875rem' },
          sizeSmall: { fontSize: '0.875rem' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 26,
          },
          sizeSmall: { height: 24, fontSize: '0.6875rem' },
          clickable: {
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: p,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: p,
              outlineOffset: 2,
              borderRadius: 2,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: '1px solid',
            borderColor: pal.divider,
            backdropFilter: 'blur(12px)',
            backgroundColor: isDark
              ? 'rgba(28, 24, 21, 0.92)'
              : 'rgba(255, 252, 250, 0.92)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: '1px solid',
            borderColor: pal.divider,
            backgroundColor: pal.background.paper,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            boxShadow: hpShadow.overlay,
            border: '1px solid',
            borderColor: pal.divider,
          },
        },
      },
      MuiTablePagination: {
        defaultProps: {
          labelRowsPerPage: 'Lignes par page',
        },
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
            color: pal.text.secondary,
          },
          toolbar: { minHeight: 48 },
          selectLabel: { fontSize: '0.8125rem' },
          displayedRows: { fontSize: '0.8125rem' },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          rowHeight: 44,
          columnHeaderHeight: 44,
          disableRowSelectionOnClick: true,
        },
        styleOverrides: {
          root: {
            border: 0,
            fontFamily: fontUi,
            fontSize: '0.875rem',
            '--DataGrid-rowBorderColor': pal.divider,
            backgroundColor: pal.background.paper,
          },
          columnHeaders: {
            backgroundColor: pal.action.hover,
            borderBottom: `1px solid ${pal.divider}`,
          },
          columnHeaderTitle: {
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            color: pal.text.secondary,
          },
          'columnHeader--sorted': {
            color: isDark ? hpColors.darkPrimary : p,
            '& .MuiDataGrid-columnHeaderTitle': {
              color: isDark ? hpColors.darkPrimary : p,
              fontWeight: 700,
            },
          },
          iconButtonContainer: {
            '& .MuiIconButton-root': {
              color: 'inherit',
            },
          },
          sortIcon: {
            color: 'inherit',
          },
          filterIcon: {
            color: isDark ? hpColors.darkPrimary : p,
          },
          cell: {
            borderColor: pal.divider,
            display: 'flex',
            alignItems: 'center',
            paddingTop: 0,
            paddingBottom: 0,
          },
          row: {
            '&:hover': {
              backgroundColor: pal.action.hover,
            },
          },
          footerContainer: {
            borderTop: `1px solid ${pal.divider}`,
            minHeight: 48,
          },
        },
      },
    },
    _client: { displayName: themeInput?.displayName || 'Happy Paws' },
  });
}
